import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { Modal } from './DigitalClientsPage';

const S = {
  page:   { padding: '32px 36px', background: '#F5F5F5', minHeight: '100%', fontFamily: "'Inter',sans-serif" },
  title:  { margin: 0, fontSize: 26, fontWeight: 800, color: '#9B7E54', fontFamily: "'Inter',sans-serif", letterSpacing: '-.01em' },
  table:  { width: '100%', borderCollapse: 'collapse', background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,.06)', border: '1px solid #E8D5BC' },
  th:     { textAlign: 'left', padding: '12px 20px', fontSize: 11, fontWeight: 700, color: '#9A8A7A', textTransform: 'uppercase', letterSpacing: '.08em', background: '#FBF7F2', borderBottom: '1px solid #E8D5BC' },
  tr:     { borderBottom: '1px solid #F0EDE8', cursor: 'pointer', transition: 'background .1s' },
  td:     { padding: '14px 20px', fontSize: 13, color: '#0A0A0A', verticalAlign: 'middle' },
  badge:  (n) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: n > 0 ? '#FBF7F2' : '#F5F5F5', color: n > 0 ? '#9B7E54' : '#B0A090', border: `1px solid ${n > 0 ? '#E8D5BC' : '#E0E0E0'}`, textDecoration: 'underline', cursor: 'pointer' }),
  btnPrimary: { background: '#C19A6B', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: "'Inter',sans-serif" },
  btnEdit:    { background: 'transparent', border: '1px solid #E0E0E0', borderRadius: 7, padding: '5px 14px', fontSize: 12, fontWeight: 600, color: '#4A4A4A', cursor: 'pointer', fontFamily: "'Inter',sans-serif" },
  btnDel:     { background: 'transparent', border: '1px solid #F0C0C0', borderRadius: 7, padding: '5px 14px', fontSize: 12, fontWeight: 600, color: '#A02020', cursor: 'pointer', fontFamily: "'Inter',sans-serif", marginLeft: 6 },
  empty:  { padding: '48px 20px', textAlign: 'center', color: '#9A8A7A', fontSize: 14 },
};

export default function DigitalDashboard() {
  const { canEdit, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({ name: '', industry: '', contact: '', notes: '' });
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');

  const load = () => api.getClients().then(setClients).catch(()=>{}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  function openCreate() { setForm({ name: '', industry: '', contact: '', notes: '' }); setModal('new'); }
  function openEdit(e, c) { e.stopPropagation(); setForm({ name: c.name, industry: c.industry||'', contact: c.contact||'', notes: c.notes||'' }); setModal(c); }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'new') await api.createClient(form);
      else await api.updateClient(modal.id, form);
      await load();
      setModal(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(e, id) {
    e.stopPropagation();
    if (!window.confirm('Delete this client?')) return;
    await api.deleteClient(id);
    load();
  }

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry||'').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={S.title}>All Clients ({clients.length})</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            placeholder="Search…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 14px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: "'Inter',sans-serif", width: 200, background: '#fff' }}
          />
          {canEdit && <button style={S.btnPrimary} onClick={openCreate}>+ New Client</button>}
        </div>
      </div>

      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Client</th>
              <th style={S.th}>Industry</th>
              <th style={S.th}>Plans</th>
              <th style={S.th}>Added</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: '#9A8A7A', padding: 48 }}>
                {search ? 'No clients match.' : 'No clients yet. Create your first client.'}
              </td></tr>
            ) : filtered.map(c => {
              const total = c.plan_count || 0;
              return (
                <tr key={c.id} style={S.tr}
                  onClick={() => navigate(`/clients/${c.id}`)}
                  onMouseEnter={e=>e.currentTarget.style.background='#FBF7F2'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ ...S.td, fontWeight: 700 }}>{c.name}</td>
                  <td style={{ ...S.td, color: '#9A8A7A' }}>{c.industry || '—'}</td>
                  <td style={S.td}>
                    <span style={S.badge(total)} onClick={e=>{e.stopPropagation();navigate(`/clients/${c.id}`);}}>
                      {total} {total === 1 ? 'plan' : 'plans'}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: '#9A8A7A' }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e=>e.stopPropagation()}>
                    {canEdit && <button style={S.btnEdit} onClick={e=>openEdit(e,c)}>Edit</button>}
                    {isAdmin && total === 0 && <button style={S.btnDel} onClick={e=>handleDelete(e,c.id)}>Delete</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <Modal title={modal==='new' ? 'New Client' : `Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          {[
            { key:'name',     label:'Client Name *', placeholder:'e.g. Linella' },
            { key:'industry', label:'Industry',       placeholder:'e.g. Retail, FMCG' },
            { key:'contact',  label:'Contact',        placeholder:'Contact person or email' },
          ].map(f=>(
            <label key={f.key} style={{ display:'block', marginBottom:14 }}>
              <span style={{ fontSize:11,fontWeight:600,color:'#9A8A7A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5,display:'block' }}>{f.label}</span>
              <input value={form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} placeholder={f.placeholder}
                style={{ width:'100%',boxSizing:'border-box',background:'#FFFFFF',border:'1px solid #E0E0E0',borderRadius:7,padding:'9px 12px',color:'#0A0A0A',fontSize:13,outline:'none',fontFamily:"'Inter',sans-serif" }} />
            </label>
          ))}
          <label style={{ display:'block', marginBottom:22 }}>
            <span style={{ fontSize:11,fontWeight:600,color:'#9A8A7A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:5,display:'block' }}>Notes</span>
            <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3}
              style={{ width:'100%',boxSizing:'border-box',background:'#FFFFFF',border:'1px solid #E0E0E0',borderRadius:7,padding:'9px 12px',color:'#0A0A0A',fontSize:13,outline:'none',fontFamily:"'Inter',sans-serif",resize:'vertical' }} />
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={{ background:'transparent',border:'1px solid #E0E0E0',borderRadius:7,padding:'8px 16px',fontSize:13,cursor:'pointer',fontFamily:"'Inter',sans-serif" }} onClick={()=>setModal(null)}>Cancel</button>
            <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>{saving?'Saving…':modal==='new'?'Create Client':'Save'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
